/**
 * SalesLeadsPage — Phase 33.9.
 *
 * Full list of all sales leads with status filter, text search
 * (name / company / email), and column-click sort.
 */

import { useState, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';

// ── Types ────────────────────────────────────────────────────────────────────

export interface LeadSummary {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  company: string;
  company_size_estimate: number | null;
  industry: string;
  linkedin_url: string | null;
  email: string | null;
  discovery_source: string;
  discovery_signal: string;
  discovered_at: number;
  pipeline_id: string | null;
  status: string;
}

interface LeadsResponse {
  leads: LeadSummary[];
  total: number;
}

export type SortField = 'discovered_at' | 'status' | 'company';
type SortDir = 'asc' | 'desc';

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchLeads(): Promise<LeadsResponse> {
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/sales/leads`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<LeadsResponse>;
}

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  new:        '#7dd3fc',
  contacted:  '#a78bfa',
  qualified:  '#6ee7b7',
  proposed:   '#fbbf24',
  won:        '#34d399',
  lost:       '#f87171',
};

const ALL_STATUSES = ['new', 'contacted', 'qualified', 'proposed', 'won', 'lost'];

// ── Helpers ──────────────────────────────────────────────────────────────────

export function statusColor(s: string): string {
  return STATUS_COLORS[s] ?? 'var(--omega-ink-subtle)';
}

export function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function sortLeads(list: LeadSummary[], field: SortField, dir: SortDir): LeadSummary[] {
  return [...list].sort((a, b) => {
    let va: string | number, vb: string | number;
    switch (field) {
      case 'discovered_at': va = a.discovered_at; vb = b.discovered_at; break;
      case 'status':        va = a.status;         vb = b.status;        break;
      case 'company':       va = a.company;        vb = b.company;       break;
    }
    if (va < vb) return dir === 'asc' ? -1 : 1;
    if (va > vb) return dir === 'asc' ?  1 : -1;
    return 0;
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4,
  color: 'var(--omega-ink)',
  fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt',
  padding: '4px 8px',
  outline: 'none',
};

function SortHeader({
  label, field, active, dir, onSort,
}: {
  label: string;
  field: SortField;
  active: boolean;
  dir: SortDir;
  onSort: (f: SortField) => void;
}) {
  return (
    <button
      onClick={() => onSort(field)}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        fontFamily: 'var(--omega-mono)',
        fontSize: '8.5pt',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: active ? '#7dd3fc' : 'var(--omega-ink-subtle)',
      }}
    >
      {label}{active ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function SalesLeadsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]             = useState('');
  const [sortField, setSortField]       = useState<SortField>('discovered_at');
  const [sortDir, setSortDir]           = useState<SortDir>('desc');

  const { data, isPending, isError } = useQuery({
    queryKey: ['sales', 'leads'],
    queryFn: fetchLeads,
    refetchInterval: 30_000,
  });

  const all = data?.leads ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = all;
    if (statusFilter) list = list.filter(l => l.status === statusFilter);
    if (q) {
      list = list.filter(l =>
        `${l.first_name} ${l.last_name}`.toLowerCase().includes(q) ||
        l.company.toLowerCase().includes(q) ||
        (l.email ?? '').toLowerCase().includes(q),
      );
    }
    return sortLeads(list, sortField, sortDir);
  }, [all, statusFilter, search, sortField, sortDir]);

  function handleSort(field: SortField) {
    if (field === sortField) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  }

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Sales', to: '/omega/sales' },
          { label: 'Leads' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Leads
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {filtered.length}{filtered.length !== all.length ? `/${all.length}` : ''}
          </span>
        )}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <input
          placeholder="Search name / company / email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: 220, flex: '1 1 220px' }}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={inputStyle}>
          <option value="">All statuses</option>
          {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(statusFilter || search) && (
          <button
            onClick={() => { setStatusFilter(''); setSearch(''); }}
            style={{ ...inputStyle, cursor: 'pointer', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Column headers */}
      {!isPending && !isError && filtered.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '90px 1fr 160px 130px',
          gap: 12,
          padding: '6px 12px',
          borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          marginBottom: 2,
        }}>
          <SortHeader label="Status"     field="status"       active={sortField === 'status'}       dir={sortDir} onSort={handleSort} />
          <span style={{ fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--omega-ink-subtle)' }}>
            Name / Title
          </span>
          <SortHeader label="Company"    field="company"      active={sortField === 'company'}      dir={sortDir} onSort={handleSort} />
          <SortHeader label="Discovered" field="discovered_at" active={sortField === 'discovered_at'} dir={sortDir} onSort={handleSort} />
        </div>
      )}

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load leads.</div>}

      {!isPending && !isError && filtered.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center' }}>
          No leads match the current filters.
        </div>
      )}

      {filtered.map(l => (
        <Link
          key={l.id}
          to={`/omega/sales/leads/${l.id}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '90px 1fr 160px 130px',
            gap: 12,
            alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
            textDecoration: 'none',
            color: 'inherit',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.03)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
        >
          <span style={{
            fontSize: '7.5pt',
            padding: '1px 6px',
            border: `1px solid ${STATUS_COLORS[l.status] ?? 'var(--omega-ink-subtle)'}`,
            color: STATUS_COLORS[l.status] ?? 'var(--omega-ink-subtle)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 700,
            borderRadius: 3,
            justifySelf: 'start',
          }}>
            {l.status}
          </span>

          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: '10pt', fontWeight: 600, color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {l.first_name} {l.last_name}
            </div>
            {l.title && (
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {l.title}
              </div>
            )}
          </div>

          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: '9pt', color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {l.company}
            </div>
            {l.industry && (
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {l.industry}
              </div>
            )}
          </div>

          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
            {fmtTime(l.discovered_at)}
          </span>
        </Link>
      ))}
    </div>
  );
}
